import { all, call, put, takeLatest } from '@redux-saga/core/effects';
import axios, { AxiosResponse } from 'axios';

import { SagaPayload } from '../api.types';
import {
  CreateTribeTemplateRequest,
  CreateTribeTemplateResponse,
  UpdateTribeTemplateRequest,
  UpdateTribeTemplateResponse,
  ViewTribeTemplateRequest,
  ViewTribeTemplateResponse
} from './tribe-template.types';

import {
  CREATING_TRIBE_TEMPLATE,
  RETRIEVING_TRIBE_TEMPLATE,
  UPDATING_TRIBE_TEMPLATE
} from './tribe-template.constants';

import { requestViewAllProgrammes } from '../programme/v2/programme.actions';
import {
  requestCreateTribeTemplate,
  requestUpdateTribeTemplate,
  requestViewTribeTemplate
} from './tribe-template.actions';

import {
  getCreateTribeTemplateRequest,
  getUpdateTribeTemplateRequest,
  getViewTribeTemplateRequest
} from './tribe-template.endpoints';

import { setTribeTemplate } from '../../slices/programme/tribe/tribe.slice';
import { addSpinner, addSystemNotice, removeSpinner } from '../../slices/system/system.slice';

import { getErrorMessage } from '../../utils/api.util';
import { ERROR, SUCCESS } from '../../utils/constants';

export function* performCreateTribeTemplateRequest({ payload }: SagaPayload<CreateTribeTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(CREATING_TRIBE_TEMPLATE));

    const [endpoint, options] = getCreateTribeTemplateRequest(requestData);

    const { data }: AxiosResponse<CreateTribeTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewTribeTemplate({
        requestData: { programmeExternalReference: requestData.programmeExternalReference }
      })
    );
    yield put(requestViewAllProgrammes());
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(CREATING_TRIBE_TEMPLATE));
  }
}

export function* watchForCreateTribeTemplateRequest() {
  yield takeLatest(requestCreateTribeTemplate.type, performCreateTribeTemplateRequest);
}

export function* performViewTribeTemplateRequest({ payload }: SagaPayload<ViewTribeTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(RETRIEVING_TRIBE_TEMPLATE));

    const [endpoint, options] = getViewTribeTemplateRequest(requestData);

    const { data }: AxiosResponse<ViewTribeTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(setTribeTemplate(data.tribeTemplates[0]));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(RETRIEVING_TRIBE_TEMPLATE));
  }
}

export function* watchForViewTribeTemplateRequest() {
  yield takeLatest(requestViewTribeTemplate.type, performViewTribeTemplateRequest);
}

export function* performUpdateTribeTemplateRequest({ payload }: SagaPayload<UpdateTribeTemplateRequest>) {
  const { requestData, onSuccess } = payload;

  try {
    yield put(addSpinner(UPDATING_TRIBE_TEMPLATE));

    const [endpoint, options] = getUpdateTribeTemplateRequest(requestData);

    const { data }: AxiosResponse<UpdateTribeTemplateResponse> = yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess, undefined);
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
    yield put(
      requestViewTribeTemplate({
        requestData: { programmeExternalReference: requestData.programmeExternalReference }
      })
    );
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner(UPDATING_TRIBE_TEMPLATE));
  }
}

export function* watchForUpdateTribeTemplateRequest() {
  yield takeLatest(requestUpdateTribeTemplate.type, performUpdateTribeTemplateRequest);
}

export default function* tribeTemplateSaga() {
  yield all([
    watchForCreateTribeTemplateRequest(),
    watchForViewTribeTemplateRequest(),
    watchForUpdateTribeTemplateRequest()
  ]);
}
